import { FC, RefObject, useContext } from "react";
import {
  EditingContext,
  NoteSections,
} from "@src/views/ClientPage/SessionNotes/EditingContext";
import DefaultMarkdownEditor from "@src/components/DefaultMarkDownEditor/DefaultMarkdownEditor";
import {
  headingsPlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  thematicBreakPlugin,
} from "@mdxeditor/editor";
import { useTranslation } from "react-i18next";
import SaveCancelButtons from "./SaveCancelButtons";
import EditableDisplay from "./EditableDisplay";

const CLICKABLE_MARKDOWN_DISPLAY_CLASS =
  "p-2 body1 bg-white text-indigo-800 hover:bg-slate-100 hover:cursor-pointer";

interface EditableMarkdownSectionProps {
  section: NoteSections;
  currentValue: string;
  editorRef: RefObject<MDXEditorMethods>;
  onSave: (newMarkdown: string) => Promise<void>;
  isSubmitting?: boolean;
}

const markdownPlugins = [
  headingsPlugin(),
  listsPlugin(),
  quotePlugin(),
  thematicBreakPlugin(),
  linkPlugin(),
];

const EditableMarkdownSection: FC<EditableMarkdownSectionProps> = ({
  section,
  currentValue,
  editorRef,
  onSave,
  isSubmitting = false,
}) => {
  const ctx = useContext(EditingContext)!;
  const { editingSection, closeEditing, handleSectionChange } = ctx;
  const { t } = useTranslation();

  if (editingSection !== section)
    return (
      <EditableDisplay
        className={CLICKABLE_MARKDOWN_DISPLAY_CLASS}
        onActivate={() => handleSectionChange(section)}
      >
        {currentValue ? (
          <MDXEditor
            markdown={currentValue}
            className="mb-3 body2 bg-white text-indigo-900"
            plugins={markdownPlugins}
            readOnly
          />
        ) : (
          <span className="text-slate-600">{t("SessionNotesPaper.None")}</span>
        )}
      </EditableDisplay>
    );

  return (
    <>
      <DefaultMarkdownEditor markdown={currentValue} editorRef={editorRef} />
      <SaveCancelButtons
        onSave={async () => {
          let content = editorRef.current?.getMarkdown() ?? "";
          content = content.replace(/(\r\n|\n){2,}/g, (match) => {
            return `\n\n${"<br />\n".repeat((match.length - 1) / 2)}`;
          });
          await onSave(content);
          closeEditing();
        }}
        onCancel={() => closeEditing()}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default EditableMarkdownSection;
